import type { GameData, LocationId, GameSpeed } from '../game/types';

export interface HotkeyCallbacks {
  onNinePadClick: (index: number) => void;
  onNextResearcher: () => void;
  onMenuToggle: () => void;
  onPauseToggle: () => void;
  onSpeedChange: (speed: GameSpeed) => void;
  onMoveToLocation: (location: LocationId) => void;
  onModalSelect: (index: number) => void;
  onModalConfirm: () => void;
  isModalOpen: () => boolean;
  isMoveModeActive: () => boolean;
  getCurrentSpeed: () => GameSpeed;
  getNextSpeed: () => GameSpeed;
  getPreviousSpeed: () => GameSpeed;
}

export class HotkeyManager {
  private callbacks: HotkeyCallbacks;
  private gameData: GameData;
  private keyMap: Map<string, () => void> = new Map();

  constructor(gameData: GameData, callbacks: HotkeyCallbacks) {
    this.gameData = gameData;
    this.callbacks = callbacks;
    this.buildKeyMap();
    this.setupEventListeners();
  }

  private buildKeyMap(): void {
    const hotkeys = this.gameData.hotkeys;

    // Nine-pad dial mappings
    const ninePadRows = [
      hotkeys.nine_pad.top_row,
      hotkeys.nine_pad.middle_row,
      hotkeys.nine_pad.bottom_row
    ];

    ninePadRows.forEach(row => {
      row.forEach((key: { code: string; index: number; label: string }) => {
        this.keyMap.set(key.code, () => {
          if (!this.callbacks.isModalOpen() && !this.callbacks.isMoveModeActive()) {
            this.callbacks.onNinePadClick(key.index);
          }
        });
      });
    });

    // Navigation
    this.keyMap.set(hotkeys.navigation.next_researcher.code, () => {
      if (!this.callbacks.isModalOpen()) {
        this.callbacks.onNextResearcher();
      }
    });

    this.keyMap.set(hotkeys.navigation.menu.code, () => {
      this.callbacks.onMenuToggle();
    });

    this.keyMap.set(hotkeys.navigation.pause_toggle.code, () => {
      if (!this.callbacks.isModalOpen()) {
        this.callbacks.onPauseToggle();
      }
    });

    // Speed controls
    this.keyMap.set(hotkeys.speed.pause.code, () => {
      if (!this.callbacks.isModalOpen()) {
        this.callbacks.onSpeedChange('pause');
      }
    });

    this.keyMap.set(hotkeys.speed.speed_1x.code, () => {
      if (!this.callbacks.isModalOpen()) {
        this.callbacks.onSpeedChange('1x');
      }
    });

    this.keyMap.set(hotkeys.speed.speed_2x.code, () => {
      if (!this.callbacks.isModalOpen()) {
        this.callbacks.onSpeedChange('2x');
      }
    });

    this.keyMap.set(hotkeys.speed.next.code, () => {
      if (!this.callbacks.isModalOpen()) {
        const nextSpeed = this.callbacks.getNextSpeed();
        this.callbacks.onSpeedChange(nextSpeed);
      }
    });

    this.keyMap.set(hotkeys.speed.previous.code, () => {
      if (!this.callbacks.isModalOpen()) {
        const previousSpeed = this.callbacks.getPreviousSpeed();
        this.callbacks.onSpeedChange(previousSpeed);
      }
    });

    // Move mode - only active when move mode is active
    this.keyMap.set(hotkeys.move_mode.cell_1.code, () => {
      if (this.callbacks.isMoveModeActive() && !this.callbacks.isModalOpen()) {
        this.callbacks.onMoveToLocation('cell_1');
      }
    });

    this.keyMap.set(hotkeys.move_mode.cell_2.code, () => {
      if (this.callbacks.isMoveModeActive() && !this.callbacks.isModalOpen()) {
        this.callbacks.onMoveToLocation('cell_2');
      }
    });

    this.keyMap.set(hotkeys.move_mode.cell_3.code, () => {
      if (this.callbacks.isMoveModeActive() && !this.callbacks.isModalOpen()) {
        this.callbacks.onMoveToLocation('cell_3');
      }
    });

    this.keyMap.set(hotkeys.move_mode.cell_4.code, () => {
      if (this.callbacks.isMoveModeActive() && !this.callbacks.isModalOpen()) {
        this.callbacks.onMoveToLocation('cell_4');
      }
    });

    this.keyMap.set(hotkeys.move_mode.control_room.code, () => {
      if (this.callbacks.isMoveModeActive() && !this.callbacks.isModalOpen()) {
        this.callbacks.onMoveToLocation('control_room');
      }
    });

    // Modal controls - only active when modal is open
    this.keyMap.set(hotkeys.modal.select_1.code, () => {
      if (this.callbacks.isModalOpen()) {
        this.callbacks.onModalSelect(0);
      } else if (this.callbacks.isMoveModeActive()) {
        this.callbacks.onMoveToLocation('cell_1');
      }
    });

    this.keyMap.set(hotkeys.modal.select_2.code, () => {
      if (this.callbacks.isModalOpen()) {
        this.callbacks.onModalSelect(1);
      } else if (this.callbacks.isMoveModeActive()) {
        this.callbacks.onMoveToLocation('cell_2');
      }
    });

    this.keyMap.set(hotkeys.modal.select_3.code, () => {
      if (this.callbacks.isModalOpen()) {
        this.callbacks.onModalSelect(2);
      } else if (this.callbacks.isMoveModeActive()) {
        this.callbacks.onMoveToLocation('cell_3');
      }
    });

    this.keyMap.set(hotkeys.modal.select_4.code, () => {
      if (this.callbacks.isModalOpen()) {
        this.callbacks.onModalSelect(3);
      } else if (this.callbacks.isMoveModeActive()) {
        this.callbacks.onMoveToLocation('cell_4');
      }
    });

    this.keyMap.set(hotkeys.modal.select_5.code, () => {
      if (this.callbacks.isModalOpen()) {
        this.callbacks.onModalSelect(4);
      } else if (this.callbacks.isMoveModeActive()) {
        this.callbacks.onMoveToLocation('control_room');
      }
    });

    this.keyMap.set(hotkeys.modal.select_6.code, () => {
      if (this.callbacks.isModalOpen()) {
        this.callbacks.onModalSelect(5);
      }
    });

    this.keyMap.set(hotkeys.modal.select_7.code, () => {
      if (this.callbacks.isModalOpen()) {
        this.callbacks.onModalSelect(6);
      }
    });

    this.keyMap.set(hotkeys.modal.select_8.code, () => {
      if (this.callbacks.isModalOpen()) {
        this.callbacks.onModalSelect(7);
      }
    });

    this.keyMap.set(hotkeys.modal.select_9.code, () => {
      if (this.callbacks.isModalOpen()) {
        this.callbacks.onModalSelect(8);
      }
    });

    this.keyMap.set(hotkeys.modal.confirm.code, () => {
      if (this.callbacks.isModalOpen()) {
        this.callbacks.onModalConfirm();
      }
    });
  }


  private setupEventListeners(): void {
    document.addEventListener('keydown', (event: KeyboardEvent) => {
      // Use code property (physical key) not key property (layout-dependent)
      const handler = this.keyMap.get(event.code);
      if (handler) {
        // Prevent default for game hotkeys
        if (!this.shouldAllowDefault(event)) {
          event.preventDefault();
        }
        handler();
      }
    });
  }

  private shouldAllowDefault(event: KeyboardEvent): boolean {
    // Allow default for some keys like Tab when in input fields
    if (event.target instanceof HTMLInputElement || 
        event.target instanceof HTMLTextAreaElement ||
        (event.target instanceof HTMLElement && event.target.isContentEditable)) {
      return true;
    }
    return false;
  }

  destroy(): void {
    // Remove event listeners if needed
    // For now, we'll keep them since they're on document
  }
}
